
import React from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage
} from '@/components/ui/breadcrumb';
import { HomeIcon } from 'lucide-react';

export interface BreadcrumbItem {
  name: string;
  url: string;
  isCurrentPage?: boolean;
}

interface BreadcrumbNavProps {
  items?: BreadcrumbItem[];
}

export const BreadcrumbNav = ({ items }: BreadcrumbNavProps) => {
  const location = useLocation();
  const path = location.pathname;
  
  // If no items provided, generate from current path
  const breadcrumbItems = items || generateBreadcrumbsFromPath(path);
  
  // Generate JSON-LD structured data for breadcrumbs
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': item.name,
      'item': item.url.startsWith('http') ? item.url : `${window.location.origin}${item.url}`
    }))
  };

  return (
    <>
      {/* Add structured data for SEO */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      {/* Visual breadcrumb component */}
      <Breadcrumb className="mb-4 px-1">
        <BreadcrumbList className="text-xs md:text-sm gap-1 md:gap-2.5 flex flex-nowrap overflow-x-auto whitespace-nowrap">
          <BreadcrumbItem className="flex-shrink-0">
            <BreadcrumbLink href="/" className="flex items-center">
              <HomeIcon className="h-3 w-3 md:h-4 md:w-4" />
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator className="[&>svg]:size-2.5 md:[&>svg]:size-3.5 flex-shrink-0" />
          
          {breadcrumbItems.slice(1).map((item, index) => (
            <React.Fragment key={item.url}>
              {item.isCurrentPage ? (
                <BreadcrumbItem className="flex-shrink-0 min-w-0">
                  <BreadcrumbPage className="text-xs md:text-sm font-medium truncate max-w-[80px] md:max-w-none whitespace-nowrap">
                    {item.name}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              ) : (
                <BreadcrumbItem className="flex-shrink-0 min-w-0">
                  <BreadcrumbLink 
                    href={item.url}
                    className="text-xs md:text-sm hover:text-foreground truncate max-w-[50px] md:max-w-none whitespace-nowrap"
                  >
                    {item.name}
                  </BreadcrumbLink>
                </BreadcrumbItem>
              )}
              
              {index < breadcrumbItems.length - 2 && (
                <BreadcrumbSeparator className="[&>svg]:size-2.5 md:[&>svg]:size-3.5 flex-shrink-0" />
              )}
            </React.Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    </>
  );
};

// Helper function to generate breadcrumbs from URL path
function generateBreadcrumbsFromPath(path: string): BreadcrumbItem[] {
  // Start with home
  const items: BreadcrumbItem[] = [
    { name: 'Home', url: '/' }
  ];
  
  // Split path into segments and build breadcrumb items
  const segments = path.split('/').filter(Boolean);
  
  if (segments.length === 0) return items;
  
  let currentPath = '';
  
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    
    // Format segment name (convert-this-format to "Convert This Format")
    let name = segment
      .replace(/-/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
      
    // Special handling for known routes
    if (segment === 'destinations') name = 'Destinations';
    if (segment === 'search') name = 'Search';
    if (segment === 'profile') name = 'Profile';
    if (segment === 'collections') name = 'Collections';
    if (segment === 'visa-requirements') name = 'Visa Requirements';
    
    // Attempt to extract readable name from IDs in URLs when possible
    // For numeric or UUID segments that are probably IDs, try to get a more readable name
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment) ||
        /^\d+$/.test(segment)) {
      // For the last segment (current page)
      if (index === segments.length - 1) {
        // Keep the default formatting for now
        // The specific pages can override this with custom items prop
      } 
      else {
        // For ID segments that are not the last one
        // Try to use the previous segment to make it more descriptive
        const previousSegment = segments[index - 1];
        if (previousSegment) {
          name = previousSegment.replace(/-/g, ' ').replace(/s$/, '');
          name = name.charAt(0).toUpperCase() + name.slice(1) + ' Details';
        }
      }
    }
    
    items.push({
      name,
      url: currentPath,
      isCurrentPage: index === segments.length - 1
    });
  });
  
  return items;
}
